import { useMemo } from 'react'
import { DefaultTheme, useTheme } from 'styled-components/native'

export type MarkdownBodyVariant = 'bodyLarge' | 'bodyNormal' | 'bodySmall'

/**
 * Add a new entry to `MarkdownBodyVariant` + `VARIANT_FONT_SIZE_KEY` if a
 * caller needs a body variant beyond the three currently supported.
 */
const VARIANT_FONT_SIZE_KEY: Record<MarkdownBodyVariant, 'large' | 'regular' | 'small'> = {
  bodyLarge: 'large',
  bodyNormal: 'regular',
  bodySmall: 'small',
}

type UseMarkdownStylesOptions = {
  variant?: MarkdownBodyVariant
  textColor?: keyof DefaultTheme['colors']
}

/**
 * Returns a `styles` map for `DialogueMarkdown` that renders paragraph and
 * bold markdown using the chosen Typography body variant — defaults to `bodyNormal`.
 * @example
 *   const markdownStyles = useMarkdownStyles({ variant: 'bodyNormal' })
 *   return <DialogueMarkdown markdown={body} styles={markdownStyles} containerStyles={{}} />
 */
export const useMarkdownStyles = ({
  variant = 'bodyNormal',
  textColor,
}: UseMarkdownStylesOptions = {}) => {
  const theme = useTheme()
  return useMemo(() => {
    const sizeKey = VARIANT_FONT_SIZE_KEY[variant]
    const base = {
      fontSize: theme.fonts.fontSize[sizeKey],
      lineHeight: theme.fonts.lineHeight[sizeKey],
    }

    // The returned object can hold style entries for any HTML tag that
    // DialogueMarkdown forwards to react-native-render-html's `tagsStyles`,
    // including: p, strong, b, i, span, a, li, ul, ol, blockquote, code, hr.
    //
    // Tags with custom renderers in DialogueMarkdown bypass `tagsStyles` and
    // can NOT be overridden through this hook, eg: h1..h6, em, img.
    const textStyle = { color: textColor ? theme.colors[textColor] : theme.colors.text }

    return {
      p: { ...base, ...textStyle, fontFamily: theme.fonts.fontTypes.light },
      strong: { ...base, ...textStyle, fontFamily: theme.fonts.fontTypes.bold },
      b: { ...base, ...textStyle, fontFamily: theme.fonts.fontTypes.bold },
    }
  }, [theme, variant, textColor])
}
